import geodist from 'geodist';

import {
  EventType,
  TriggerType,
  getStandAlonePageName,
  getPageName,
  logAnalyticsEvent,
  PageTags,
} from '@pass-frontend/common/analytics';
import {
  FacilityOffering,
  Geolocation,
} from '@pass-frontend/data-access/common/edge';
import { Coordinates } from './gyms-details-view';

export interface ViewportPropsForMap {
  latitude: number;
  longitude: number;
  zoom?: number;
  width?: string;
  height?: string;
}

export const distanceOptions = [
  { label: '5 miles', value: 'distance:within_5_miles' },
  { label: '10 miles', value: 'distance:within_10_miles' },
  { label: '20 miles', value: 'distance:within_20_miles' },
  { label: '30 miles', value: 'distance:within_30_miles' },
  { label: '40 miles', value: 'distance:within_40_miles' },
];

export const getDistance = (distance: string): number => {
  switch (distance) {
    case 'distance:within_5_miles':
      return 5;
    case 'distance:within_10_miles':
      return 10;
    case 'distance:within_20_miles':
      return 20;
    case 'distance:within_30_miles':
      return 30;
    case 'distance:within_40_miles':
      return 40;
    default:
      return 5;
  }
};

export const getZoomForSelectedDistance = (
  selctedDistance: number,
  isDesktop: boolean,
): number => {
  if (isDesktop) {
    switch (selctedDistance) {
      case 5:
        return 11;
      case 10:
        return 10.5;
      case 20:
        return 9.5;
      case 30:
        return 9;
      case 40:
        return 8.5;
      default:
        return 11;
    }
  }
  switch (selctedDistance) {
    case 5:
      return 9;
    case 10:
      return 8.5;
    case 20:
      return 8;
    case 30:
      return 7.5;
    case 40:
      return 7;
    default:
      return 9;
  }
};

export const getZoomForMobileView = (
  gym: FacilityOffering,
  searchedCoordinates: Coordinates,
) => {
  const farthestGymDistance = getDistanceInMiles(gym, searchedCoordinates);
  if (farthestGymDistance <= 2) {
    return 11.5;
  }
  if (farthestGymDistance <= 3) {
    return 11;
  }
  if (farthestGymDistance <= 5) {
    return 10;
  }
  if (farthestGymDistance <= 10) {
    return 9;
  }
  if (farthestGymDistance <= 20) {
    return 8;
  }
  if (farthestGymDistance <= 30) {
    return 7.5;
  }
  return 7;
};

export const getDistanceInMiles = (
  gym: FacilityOffering,
  fromCoordinates: Coordinates,
) => {
  const {
    location: { latitude, longitude },
  } = gym;
  const toLocation: Coordinates = {
    latitude: latitude ?? 0,
    longitude: longitude ?? 0,
  };
  return getDistanceFromSelectedGym(toLocation, fromCoordinates);
};

export const getDistanceFromSelectedGym = (
  fromLocation: Coordinates | undefined,
  toLocation: Geolocation | undefined,
) => {
  const distanceInMiles = geodist(
    toGeoCordinates(fromLocation),
    toGeoCordinates(toLocation),
    {
      exact: true,
      unit: 'mi',
    },
  );

  // precision to 1/100th of a mile under 10 miles, to 1/10th of a mile over 10 miles, 1 if over 100 miles
  return distanceInMiles < 10
    ? Math.round(distanceInMiles * 100) / 100
    : distanceInMiles < 100
      ? Math.round(distanceInMiles * 10) / 10
      : Math.round(distanceInMiles);
};

const toGeoCordinates = (coordinates: Geolocation | undefined) => {
  return [coordinates?.latitude, coordinates?.longitude];
};

export const logAnalysticsForGymSearch = (
  selectedDistance: number,
  selectedAddress: string,
  gymName: string,
  dataUiElement: string,
  path: string,
  isOnePass?: boolean,
) => {
  /* eslint-disable no-restricted-globals, @typescript-eslint/ban-ts-comment */
  /* @ts-ignore  Type error: Property 'setAttribute' does not exist on type 'EventTarget'. */
  if (event?.target && event?.target?.setAttribute) {
    /* eslint-disable no-restricted-globals, @typescript-eslint/ban-ts-comment */
    /* @ts-ignore  Type error: Property 'setAttribute' does not exist on type 'EventTarget'. */
    event?.target?.setAttribute('data-ui-element-name', dataUiElement);
  }

  logAnalyticsEvent(
    EventType.PageNav,
    {
      pageName: isOnePass ? getPageName(path) : getStandAlonePageName(path),
      pageTags: {
        [PageTags.NewPageLoad]: false,
        [PageTags.IsFilter]: true,
        [PageTags.FilterName]:
          gymName !== ''
            ? 'Distance || Address or ZIP code || Gym name (optional)'
            : 'Distance || Address or ZIP code',
        [PageTags.FilterValues]:
          gymName !== ''
            ? selectedDistance + ' || ' + selectedAddress + ' || ' + gymName
            : selectedDistance + ' || ' + selectedAddress,
      },
    },
    event,
  );
};

export const logCustomUserEventForGymSearchForError = (
  errorMessage: string,
  dataUiElementName: string,
  path: string,
  isOnePass?: boolean,
) => {
  const ErrorMessages = [errorMessage];
  logAnalyticsEvent(EventType.CustomUserEvent, {
    type: 'Form error',
    pageName: isOnePass ? getPageName(path) : getStandAlonePageName(path),
    [PageTags.NewPageLoad]: false,
    errors: ErrorMessages,
    'Trigger UI Element Name': dataUiElementName,
    additionalData: TriggerType.Click,
  });
};
